<template>
    <div class="account-query-container">
        <!-- 查询条件区域 -->
        <el-card class="query-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span>创意查询</span>
                </div>
            </template>

            <el-form :model="queryParams" ref="queryForm" :inline="true" class="query-form">
                <el-form-item label="账户名称" prop="accountName">
                    <el-input type="textarea" v-model="queryParams.accountName" style="width: 200px; text-align: left;"
                        placeholder="每行一个账户名称" :rows="3">
                    </el-input>
                </el-form-item>

                <el-form-item label="区域" prop="area">
                    <el-select v-model="queryParams.area" placeholder="请选择区域" style="width: 150px">
                        <el-option v-for="item in regionOptions" :key="item.value" :label="item.label"
                            :value="item.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="时间范围" prop="timeRange">
                    <el-date-picker v-model="queryParams.timeRange" type="daterange" range-separator="至"
                        start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" format="yyyy-MM-dd"
                        style="width: 400px" />
                    <div style="margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap;">
                        <el-button size="mini" :type="activeShortcut === 'today' ? 'primary' : ''" @click="setTimeShortcut('today')">今日</el-button>
                        <el-button size="mini" :type="activeShortcut === 'yesterday' ? 'primary' : ''" @click="setTimeShortcut('yesterday')">昨日</el-button>
                        <el-button size="mini" :type="activeShortcut === 'last7days' ? 'primary' : ''" @click="setTimeShortcut('last7days')">最近7天</el-button>
                        <el-button size="mini" :type="activeShortcut === 'thisMonth' ? 'primary' : ''" @click="setTimeShortcut('thisMonth')">本月</el-button>
                        <el-button size="mini" :type="activeShortcut === 'lastMonth' ? 'primary' : ''" @click="setTimeShortcut('lastMonth')">上个月</el-button>
                    </div>
                </el-form-item>

                <el-form-item label="创意筛选时间" prop="creativeTimeRange">
                    <el-date-picker v-model="queryParams.creativeTimeRange" type="daterange" range-separator="至"
                        start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" format="yyyy-MM-dd"
                        style="width: 400px" />
                </el-form-item>

                <el-form-item>
                    <el-button type="primary" @click="handleQuery" icon="el-icon-search">
                        查询
                    </el-button>
                    <el-button @click="handleReset" icon="el-icon-refresh">
                        重置
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 查询结果区域 -->
        <el-card class="result-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span>查询结果</span>
                    <div>
                        <el-button @click="handleRefresh" icon="el-icon-refresh">
                            刷新
                        </el-button>
                        <el-button type="primary" icon="el-icon-download" @click="handleExport">
                            导出Excel
                        </el-button>
                    </div>
                </div>
            </template>

            <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
                <el-table-column prop="accountName" label="账户名称" min-width="120" show-overflow-tooltip />
                <el-table-column prop="id" label="创意ID" width="100" align="center" />
                <el-table-column prop="title" label="文案" min-width="200" show-overflow-tooltip />
                <el-table-column prop="createDate" label="创建日期" width="180" align="center" />
                
                <!-- 统计列：花费 -->
                <el-table-column prop="spent" label="花费" width="120" align="center">
                    <template slot-scope="scope">
                        <span v-if="scope.row.spent > 0">
                            ¥{{ scope.row.spent.toFixed(2) }}
                        </span>
                        <span v-else>¥0.00</span>
                    </template>
                </el-table-column>
                
                <!-- 新增统计列：曝光量 -->
                <el-table-column prop="showCount" label="曝光量" width="100" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.showCount || 0 }}
                    </template>
                </el-table-column>
                
                <!-- 新增统计列：点击量 -->
                <el-table-column prop="clickCount" label="点击量" width="100" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.clickCount || 0 }}
                    </template>
                </el-table-column>
                
                <!-- 新增统计列：点击率 -->
                <el-table-column prop="clickRateStr" label="点击率" width="80" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.clickRateStr || '0%' }}
                    </template>
                </el-table-column>
                
                <!-- 新增统计列：CPM -->
                <el-table-column prop="cpm" label="CPM" width="80" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.cpm ? '¥' + scope.row.cpm.toFixed(2) : '-' }}
                    </template>
                </el-table-column>
                
                <!-- 新增统计列：CPC -->
                <el-table-column prop="cpc" label="CPC" width="80" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.cpc ? '¥' + scope.row.cpc.toFixed(2) : '-' }}
                    </template>
                </el-table-column>

                <el-table-column prop="statusDesc" label="状态" width="100" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="getStatusType(scope.row.statusDesc)" size="small">
                            {{ scope.row.statusDesc }}
                        </el-tag>
                    </template>
                </el-table-column>
                
                <el-table-column prop="avatarFullUrl" label="头像素材" width="120" align="center">
                    <template slot-scope="scope">
                        <el-image v-if="scope.row.avatarFullUrl" :src="scope.row.avatarFullUrl"
                            :preview-src-list="[scope.row.avatarFullUrl]"
                            style="width: 40px; height: 40px; border-radius: 4px;">
                            <div slot="error" class="image-slot">
                                <i class="el-icon-picture-outline"></i>
                            </div>
                        </el-image>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                
                <el-table-column prop="imgsFullUrl" label="图片素材" width="120" align="center">
                    <template slot-scope="scope">
                        <el-image v-if="scope.row.imgsFullUrl" :src="scope.row.imgsFullUrl"
                            :preview-src-list="[scope.row.imgsFullUrl]"
                            style="width: 40px; height: 40px; border-radius: 4px;">
                            <div slot="error" class="image-slot">
                                <i class="el-icon-picture-outline"></i>
                            </div>
                        </el-image>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                
                <el-table-column prop="videoFullUrl" label="视频素材" width="120" align="center">
                    <template slot-scope="scope">
                        <div v-if="scope.row.videoFullUrl">
                            <el-button type="text" @click="handlePreviewVideo(scope.row.videoFullUrl)">
                                预览视频
                            </el-button>
                        </div>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                
                <el-table-column prop="campaignName" label="广告系列" min-width="150" show-overflow-tooltip />
                <el-table-column prop="groupName" label="广告组" min-width="150" show-overflow-tooltip />
                
                <el-table-column label="操作" width="120" fixed="right">
                    <template slot-scope="scope">
                        <el-button type="text" size="small" @click="handleView(scope.row)">
                            详情
                        </el-button>
                        <el-button type="text" size="small" @click="handleCopyUrl(scope.row)">
                            复制链接
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <el-pagination class="pagination" @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="pagination.total" />
        </el-card>

        <!-- 查看详情对话框 -->
        <el-dialog title="创意详情" :visible.sync="dialogVisible" width="800px">
            <el-descriptions :column="2" border v-if="currentRow">
                <el-descriptions-item label="账户名称">
                    {{ currentRow.accountName }}
                </el-descriptions-item>
                <el-descriptions-item label="创意ID">
                    {{ currentRow.id }}
                </el-descriptions-item>
                <el-descriptions-item label="创建日期">
                    {{ currentRow.createDate }}
                </el-descriptions-item>
                <el-descriptions-item label="状态">
                    <el-tag :type="getStatusType(currentRow.statusDesc)">
                        {{ currentRow.statusDesc }}
                    </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="广告系列">
                    {{ currentRow.campaignName }}
                </el-descriptions-item>
                <el-descriptions-item label="广告组">
                    {{ currentRow.groupName }}
                </el-descriptions-item>
                <el-descriptions-item label="产品名称">
                    {{ currentRow.productName }}
                </el-descriptions-item>
                <el-descriptions-item label="文案" :span="2">
                    {{ currentRow.title }}
                </el-descriptions-item>
                <el-descriptions-item label="头像素材" :span="2">
                    <div v-if="currentRow.avatarFullUrl">
                        <el-image :src="currentRow.avatarFullUrl" style="max-width: 200px; max-height: 200px;" />
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.avatarFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="图片素材" :span="2">
                    <div v-if="currentRow.imgsFullUrl">
                        <el-image :src="currentRow.imgsFullUrl" style="max-width: 200px; max-height: 200px;" />
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.imgsFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="视频素材" :span="2">
                    <div v-if="currentRow.videoFullUrl">
                        <video :src="currentRow.videoFullUrl" controls style="max-width: 300px; max-height: 200px;"></video>
                        <div style="margin-top: 8px;">
                            <el-button type="text" @click="handleCopyText(currentRow.videoFullUrl)">
                                复制链接
                            </el-button>
                        </div>
                    </div>
                    <span v-else>-</span>
                </el-descriptions-item>
            </el-descriptions>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 视频预览对话框 -->
        <el-dialog title="视频预览" :visible.sync="videoDialogVisible" width="600px">
            <video v-if="currentVideoUrl" :src="currentVideoUrl" controls style="width: 100%; max-height: 400px;"></video>
            <span slot="footer" class="dialog-footer">
                <el-button @click="videoDialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import axios from "axios";
export default {
    name: 'CreativeQuery',
    data() {
        return {
            regionOptions: [
                { label: "浙江", value: "1" },
                { label: "福建", value: "2" }
            ],
            queryParams: {
                accountName: '',
                area: '',
                timeRange: [],
                creativeTimeRange: []
            },
            tableData: [],
            allTableData: [],       // 存储全量数据，用于前端分页
            loading: false,
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            dialogVisible: false,
            videoDialogVisible: false,
            currentRow: null,
            currentVideoUrl: '',
            cookieList: [],
            AllCookieList: [],
            activeShortcut: ''
        }
    },

    methods: {
        async findIds() {
            if (!this.queryParams.accountName || this.queryParams.accountName.trim() === '') {
                this.$message.error('请输入账户名称');
                return;
            }
            if (!this.queryParams.area) {
                this.$message.error('请选择区域');
                return;
            }
            if (!this.queryParams.timeRange || this.queryParams.timeRange.length !== 2) {
                this.$message.error('请选择时间范围');
                return;
            }

            let [startTime, endTime] = this.queryParams.timeRange;
            let list = this.queryParams.accountName.split('\n')
                .filter(line => line.trim())
                .filter((item, index, array) => array.indexOf(item) === index);

            const requestData = {
                textList: list,
                area: this.queryParams.area,
                startDate: startTime,
                endDate: endTime,
                creativeStart: this.queryParams.creativeTimeRange && this.queryParams.creativeTimeRange.length === 2 ? this.queryParams.creativeTimeRange[0] : '',
                creativeEnd: this.queryParams.creativeTimeRange && this.queryParams.creativeTimeRange.length === 2 ? this.queryParams.creativeTimeRange[1] : ''
            };

            console.log('请求参数:', requestData);
            try {
                this.loading = true;
                const response = await this.$axios.post('https://control.zjwec.cn/api/vivo/tokenToCookie', requestData);
                if (response.data.code == '0' || response.data.success) {
                    const creativeData = response.data.data;
                    console.log('获取到的创意数据:', creativeData);
                    this.processCreativeData(creativeData);
                } else {
                    this.$message.error('接口返回错误: ' + response.data.message);
                }
            } catch (error) {
                console.error('请求失败:', error);
                this.$message.error('请求失败: ' + error.message);
            } finally {
                this.loading = false;
            }
        },

        processCreativeData(creativeData) {
            // 创意列表直接从 creativeDataList 数组获取
            const creativeList = creativeData.creativeDataList || [];
            
            // 补充可能缺失的统计字段并添加账户信息
            const allCreatives = creativeList.map(creative => ({
                ...creative,
                accountName: creative.accountName || '未知账户',
                campaignName: creative.campaignName || '',
                groupName: creative.groupName || '',
                spent: creative.spent || 0,
                showCount: creative.showCount || 0,
                clickCount: creative.clickCount || 0,
                clickRateStr: creative.clickRateStr || '0%',
                cpm: creative.cpm || 0,
                cpc: creative.cpc || 0
            }));

            // 按创建日期降序排列
            allCreatives.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

            this.allTableData = allCreatives;       // 全量数据
            this.pagination.total = allCreatives.length;
            this.pagination.currentPage = 1;        // 重置到第一页
            this.updatePageData();
        },

        // 根据分页参数切片数据
        updatePageData() {
            const { currentPage, pageSize } = this.pagination;
            const start = (currentPage - 1) * pageSize;
            this.tableData = this.allTableData.slice(start, start + pageSize);
        },

        setTimeShortcut(type) {
            this.activeShortcut = type;
            const today = new Date();
            const formatDate = (d) => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            let start, end;
            switch (type) {
                case 'today':
                    start = formatDate(today);
                    end = formatDate(today);
                    break;
                case 'yesterday':
                    const yesterday = new Date(today);
                    yesterday.setDate(today.getDate() - 1);
                    start = formatDate(yesterday);
                    end = formatDate(yesterday);
                    break;
                case 'last7days':
                    const last7 = new Date(today);
                    last7.setDate(today.getDate() - 6);
                    start = formatDate(last7);
                    end = formatDate(today);
                    break;
                case 'thisMonth':
                    start = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
                    end = formatDate(today);
                    break;
                case 'lastMonth':
                    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                    start = formatDate(lastMonthStart);
                    end = formatDate(lastMonthEnd);
                    break;
                default:
                    return;
            }
            this.queryParams.timeRange = [start, end];
        },

        handleQuery() {
            if (this.queryParams.timeRange && this.queryParams.timeRange.length === 2) {
                const [startTime, endTime] = this.queryParams.timeRange;
                if (new Date(startTime) > new Date(endTime)) {
                    this.$message.error('开始时间不能大于结束时间');
                    return;
                }
            }
            this.findIds();
        },

        handleReset() {
            this.queryParams = {
                accountName: '',
                area: '',
                timeRange: [],
                creativeTimeRange: []
            };
            this.activeShortcut = '';
            this.tableData = [];
            this.allTableData = [];
            this.pagination.total = 0;
            this.pagination.currentPage = 1;
        },

        handleRefresh() {
            this.handleQuery();
        },

        handleSizeChange(size) {
            this.pagination.pageSize = size;
            this.pagination.currentPage = 1;
            this.updatePageData();
        },

        handleCurrentChange(page) {
            this.pagination.currentPage = page;
            this.updatePageData();
        },

        handleView(row) {
            this.currentRow = row;
            this.dialogVisible = true;
        },

        handlePreviewVideo(videoUrl) {
            this.currentVideoUrl = videoUrl;
            this.videoDialogVisible = true;
        },

        handleCopyUrl(row) {
            const textToCopy = `账户名称: ${row.accountName}\n创意ID: ${row.id}\n文案: ${row.title}\n创建日期: ${row.createDate}\n头像素材: ${row.avatarFullUrl || '无'}\n图片素材: ${row.imgsFullUrl || '无'}\n视频素材: ${row.videoFullUrl || '无'}`;
            this.handleCopyText(textToCopy);
        },

        handleCopyText(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.$message.success('已复制到剪贴板');
        },

        handleExport() {
            if (this.allTableData.length === 0) {
                this.$message.warning('没有数据可导出');
                return;
            }

            import('xlsx').then(XLSX => {
                const excelData = this.allTableData.map(row => ({
                    '账户名称': row.accountName || '',
                    '创意ID': row.id,
                    '文案': row.title,
                    '创建日期': row.createDate,
                    '花费': row.spent > 0 ? `¥${row.spent.toFixed(2)}` : '¥0.00',
                    '曝光量': row.showCount || 0,
                    '点击量': row.clickCount || 0,
                    '点击率': row.clickRateStr || '0%',
                    'CPM': row.cpm ? `¥${row.cpm.toFixed(2)}` : '-',
                    'CPC': row.cpc ? `¥${row.cpc.toFixed(2)}` : '-',
                    '状态': row.statusDesc,
                    '头像素材': row.avatarFullUrl || '',
                    '图片素材': row.imgsFullUrl || '',
                    '视频素材': row.videoFullUrl || '',
                    '广告系列': row.campaignName || '',
                    '广告组': row.groupName || '',
                    '产品名称': row.productName || ''
                }));

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);

                const colWidths = [
                    { wch: 15 }, { wch: 10 }, { wch: 30 }, { wch: 18 },
                    { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
                    { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 40 },
                    { wch: 40 }, { wch: 40 }, { wch: 25 }, { wch: 25 }, { wch: 15 }
                ];
                ws['!cols'] = colWidths;
                XLSX.utils.book_append_sheet(wb, ws, '创意数据');

                const fileName = `创意数据_${new Date().toISOString().split('T')[0]}.xlsx`;
                XLSX.writeFile(wb, fileName);

                this.$message.success('Excel导出成功');
            }).catch(error => {
                console.error('导出Excel失败:', error);
                this.$message.error('导出Excel失败');
            });
        },

        getStatusType(status) {
            const statusMap = {
                '待审核': 'warning',
                '审核不通过': 'danger',
                '审核通过': 'success',
                '已上线': 'success',
                '已下线': 'info'
            };
            return statusMap[status] || 'info';
        }
    }
}
</script>

<style scoped>
.account-query-container {
    padding: 20px;
}

.query-card {
    margin-bottom: 20px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.query-form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.pagination {
    margin-top: 20px;
    text-align: right;
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
}
</style>


// WEBPACK FOOTER //
// src/components/vivo/creativeinfo.vue